import React, { Component } from 'react';
import { ApplicationState } from 'store/rootReducer';
import { connect, ResolveThunks } from 'react-redux';
import Dropdown from 'react-bootstrap/Dropdown';
import { FormControl } from 'react-bootstrap';
import { AccountSummary } from 'types/rest/blazeMeterApiResponse';
import { switchAccountThunk } from 'store/blazeUserConfig/blazeUserConfigActions';
import AccountToggle from 'components/ui/accountSelect/toggle/AccountToggle';
import { WithTranslation, withTranslation } from 'react-i18next';
import BzmIcon from '../../icons/BzmIcon';

const mapStateToProps = (state: ApplicationState) => {
  const blazeUserConfig = state.blazeUserConfig.blazeUserConfig;
  if (blazeUserConfig) {
    return {
      activeAccountId: blazeUserConfig.user.activeAccountId,
      accounts: blazeUserConfig.user.accounts,
    };
  }
};

const mapDispatchToProps = {
  switchAccount: switchAccountThunk,
};

type StateProps = {
  activeAccountId: number;
  accounts: AccountSummary[];
};

export type Props = StateProps & ResolveThunks<typeof mapDispatchToProps> & WithTranslation;

type State = {
  accountNameFilter: string;
  accountFilterShown: boolean;
};

export class AccountSelect extends Component<Props, State> {
  state = {
    accountNameFilter: '',
    accountFilterShown: false,
  };

  pickerMenuStyles = {
    minWidth: '300px',
    width: '100%',
    zIndex: 3000,
  };

  changeAccount = (id: number) => {
    this.props.switchAccount(id);
  };

  handleAccountFilterChange = (event: any) => {
    const searchValue = event.currentTarget.value as string;
    this.setState({
      accountNameFilter: searchValue,
    });
  };

  handleAccountFilterReset = () => {
    this.setState({ accountNameFilter: '' });
  };

  handleAccountFilterToggle = () => {
    this.setState(prevState => ({
      accountFilterShown: !prevState.accountFilterShown,
      accountNameFilter: '',
    }));
  };

  render() {
    const { t } = this.props;
    const activeAccount = this.props.accounts.find(account => account.id === this.props.activeAccountId);

    const accountItems = this.props.accounts.map((account: AccountSummary) => {
      if (this.state.accountNameFilter !== '') {
        if (account.name.toLowerCase().indexOf(this.state.accountNameFilter.toLowerCase()) === -1) {
          return null;
        }
      }
      return (
        <Dropdown.Item
          id={`qa-account-item-${account.id}`}
          onClick={() => this.changeAccount(account.id)}
          key={account.id}
        >
          {this.props.activeAccountId === account.id && (
            <span className="indicator indicator-active indicator-sm float-right mt-3">
              <span className="sr-only">{t('accountSelect.activeIndicator')}</span>
            </span>
          )}
          {account.name}
        </Dropdown.Item>
      );
    });

    // header to be displayed when the search is open
    const searchOpenHeader = (
      <div className="dropdown-header">
        <span
          className="cursor-pointer float-right"
          aria-label={t('actions.close')}
          onClick={() => this.handleAccountFilterToggle()}
          role="button"
        >
          <BzmIcon variant={'icon-bzm-close-remove'}></BzmIcon>
          <span className="sr-only">{t('actions.close')}</span>
        </span>
        <span className="dropdown-title">{t('accountSelect.accountsTitle')}</span>
        <div className="has-form-control-icon has-form-control-icon-left mt-1">
          <FormControl
            autoFocus
            autoComplete="off"
            placeholder={t('actions.search')}
            style={{ width: '100%', paddingRight: '30px' }}
            aria-label={t('actions.search')}
            value={this.state.accountNameFilter}
            onChange={this.handleAccountFilterChange}
            id="qa-input-account-search"
          />
          <span className="form-control-icon form-control-icon-left">
            <BzmIcon variant={'icon-bzm-search'} className={'icon-theme-gray-50'} />
            <span className="sr-only">{t('actions.search')}</span>
          </span>
          {this.state.accountNameFilter !== '' && (
            <button
              onClick={() => this.handleAccountFilterReset()}
              className="form-control-icon form-control-icon-right btn btn-link btn-no-hover"
              style={{ padding: 0, pointerEvents: 'all', textDecoration: 'none' }}
            >
              <span
                className="icon icon-bzm-close icon-theme-gray-50"
                style={{ fontSize: '.7rem' }}
                aria-hidden="true"
              />
              <span className="sr-only">{t('actions.clear')}</span>
            </button>
          )}
        </div>
      </div>
    );

    // header to be displayed when the search is closed
    const searchClosedHeader = (
      <div>
        <h6 className="dropdown-header" style={{ position: 'relative' }}>
          <span
            id="qa-btn-account-search-toggle"
            className="cursor-pointer float-right"
            aria-label={t('actions.search')}
            onClick={() => this.handleAccountFilterToggle()}
            role="button"
          >
            <BzmIcon variant={'icon-bzm-search'} />
            <span className="sr-only">{t('actions.search')}</span>
          </span>
          <span className="dropdown-title">{t('accountSelect.accountsTitle')}</span>
        </h6>
      </div>
    );

    return (
      <Dropdown>
        <Dropdown.Toggle id="account-select" as={AccountToggle}>
          {activeAccount ? activeAccount.name : ''}
        </Dropdown.Toggle>

        <Dropdown.Menu style={this.pickerMenuStyles}>
          {this.state.accountFilterShown ? searchOpenHeader : searchClosedHeader}
          <div className="dropdown-menu-limited">{accountItems}</div>
        </Dropdown.Menu>
      </Dropdown>
    );
  }
}

export default withTranslation('common')(connect(mapStateToProps, mapDispatchToProps)(AccountSelect));
